import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

type ThemeContextType = {
  isDark: boolean
  toggleTheme: () => void
  theme: 'light' | 'dark'
}

// Sentinel value so useTheme() can detect a missing provider and throw.
// Storing null is preferable to a "light theme default" because a silent
// default would mask the provider-wiring bug it's meant to catch.
const ThemeContext = createContext<ThemeContextType | null>(null)

export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext)
  if (ctx === null) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}

type ThemeProviderProps = {
  children: ReactNode
}

export const ThemeProvider = ({
  children,
}: ThemeProviderProps): React.JSX.Element => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      return savedTheme === 'dark'
    }

    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    const root = window.document.documentElement
    if (isDark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Always save to localStorage to maintain sync
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
  }, [isDark])

  // Always listen for system theme changes and update local state
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      setIsDark(e.matches)
    }

    mediaQuery.addEventListener('change', handleSystemThemeChange)
    return () =>
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }, [])

  const toggleTheme = () => {
    setIsDark(!isDark)
  }

  const theme = isDark ? 'dark' : 'light'

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  )
}
